/** A screen that throws says so, rather than rendering nothing.
 *
 * Added after the inventory register white-screened on a field the API had not yet been restarted
 * to send. The cause was mundane; the **behaviour** was not — the whole app went blank, and the
 * router itself had been warning that no `errorComponent` was set.
 *
 * `enterprise-product/design/01-experience-and-screen-contracts.md` states the rule this breaks:
 * *"Loading, failure, unsupported method and missing evidence are not empty-success states."* A
 * blank page is the worst of them, because it looks like a workspace with no data.
 *
 * **This is the treatment for a screen that threw — not for a read that failed.** Those are two
 * different failures with two different remedies, and each has exactly one home: a render error
 * lands here (the router's `errorComponent`); a refused or dropped read lands in
 * `components/Loaded`'s `ReadFailed`, beside the panel that wanted the data, with a retry that
 * asks again. `screenStates.test.tsx` enumerates every screen and asserts the second one — and
 * counts a crash into *this* component as a failure of that test, not a pass: a screen that
 * crashes when a read fails has not told its reader that the read failed.
 *
 * It deliberately does **not** show the stack. A reader needs to know the screen failed, that
 * their data is untouched, and how to get back; a stack trace tells them none of those and may
 * carry a value from the response.
 */

import { WarningCircle } from "@phosphor-icons/react";

import { useDocumentTitle } from "./documentTitle";
import styles from "./ScreenFailed.module.css";

export function ScreenFailed({ reset }: { reset: () => void }) {
  useDocumentTitle("This screen could not be displayed");
  return (
    <main className={styles.main}>
      {/* `role="alert"` on a wrapper and **not** on `<main>`: this replaces the whole screen, and
          a replacement nobody announces is a blank page to anyone who cannot see it change — but
          a `<main>` given another role stops being the landmark the skip link lands on. */}
      <div role="alert" data-testid="screen-failed" className={styles.card}>
        <h1 className={styles.title}>
          <WarningCircle className={styles.icon} size={20} weight="fill" aria-hidden="true" />
          This screen could not be displayed.
        </h1>
        <p className={styles.body}>
          Nothing was changed and no figure was affected - this is a display failure, not a data
          one. Reloading usually clears it. If it does not, the API may be running an older
          version than this page expects.
        </p>
        <button type="button" className={`num ${styles.retry}`} onClick={reset}>
          Try again
        </button>
      </div>
    </main>
  );
}
